import { getLanguageLabel } from './languages';
import { normalizeJobStatus } from './transcriptionStatus';

export const REGENERATION_REASONS = [
  { code: 'appellate_review', label: 'Appellate Review' },
  { code: 'reader_preference', label: 'Reader Preference' },
  { code: 'correction', label: 'Correction' },
  { code: 'other', label: 'Other' },
];

export const getReasonLabel = (code) => {
  if (!code) return '';
  const match = REGENERATION_REASONS.find(
    (r) => r.code === code || r.label.toLowerCase() === String(code).toLowerCase()
  );
  return match?.label || String(code).replace(/_/g, ' ');
};

const TERMINAL_JOB_STATUSES = new Set(['completed', 'failed', 'cancelled']);

export const isTerminalJobStatus = (status) => {
  const normalised = normalizeJobStatus(status);
  return normalised === 'complete' || normalised === 'failed' || TERMINAL_JOB_STATUSES.has(normalised);
};

export const normalizeLanguageVersion = (version) => ({
  id: version.id || version.transcription_id,
  language: getLanguageLabel(version.language || version.regeneration_language),
  language_code: version.language_code || version.language,
  created_at: version.created_at || version.completed_at || version.regenerated_at,
  status: normalizeJobStatus(version.status || 'completed'),
  transcript_status: version.transcript_status || (version.is_certified ? 'certified' : 'draft'),
  is_certified: Boolean(version.is_certified || version.transcript_status === 'certified'),
  is_regenerated: Boolean(version.is_regenerated || version.parent_transcription_id),
  regeneration_reason: version.regeneration_reason || '',
  regeneration_reason_label:
    version.regeneration_reason_label || getReasonLabel(version.regeneration_reason),
  regeneration_requested_by: version.regeneration_requested_by || '',
  parent_transcription_id: version.parent_transcription_id || null,
});

/** Build language version list from API payload or linked records */
export const resolveLanguageVersions = (item, relatedRecords = []) => {
  if (Array.isArray(item?.language_versions) && item.language_versions.length > 0) {
    return item.language_versions.map(normalizeLanguageVersion);
  }

  const assetId = item?.audio_asset_id || item?.metadata?.audio_asset_id;
  const selfId = item?.id || item?.transcription_id;
  const parentId = item?.parent_transcription_id;

  const linked = relatedRecords.filter((record) => {
    const recordId = record.id || record.transcription_id;
    if (recordId === selfId) return true;
    if (parentId && recordId === parentId) return true;
    if (record.parent_transcription_id === selfId) return true;
    if (parentId && record.parent_transcription_id === parentId) return true;
    if (assetId && record.audio_asset_id === assetId) return true;
    return false;
  });

  if (linked.length <= 1 && !item?.is_regenerated && !parentId) {
    return item ? [normalizeLanguageVersion({ ...item, id: selfId })] : [];
  }

  const seen = new Set();
  return linked
    .filter((record) => {
      const id = record.id || record.transcription_id;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    })
    .map(normalizeLanguageVersion)
    .sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
};

export const getExistingLanguageLabels = (languageVersions) =>
  new Set(
    (languageVersions || []).map((v) => getLanguageLabel(v.language).toLowerCase())
  );

export const hasPendingLanguageVersions = (languageVersions) =>
  (languageVersions || []).some((v) => !isTerminalJobStatus(v.status));
